import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useId } from 'react';
import { ClientProjectOverview } from '@models/client.model';
import { useTranslation } from '@hooks/useTranslation';
import { pluralize } from './clientProgressFormat';
import { fillTemplate } from './clientProjectDisplay';

export type ClientAttentionKind = 'awaitingApproval' | 'openIncidents';

interface ClientAttentionBlockProps {
  /** Overview rows in display order. */
  overviews: ClientProjectOverview[];
  onNavigate: (projectId: string, kind: ClientAttentionKind) => void;
}

const MAX_ROWS = 5;

interface AttentionRow {
  projectId: string;
  projectName: string;
  kind: ClientAttentionKind;
  count: number;
}

/** One row per (project, kind): awaiting-approval rows first, then incidents. */
export const buildAttentionRows = (
  overviews: ClientProjectOverview[],
): AttentionRow[] => {
  const rows: AttentionRow[] = [];
  for (const o of overviews) {
    if (o.awaitingClientApprovalCount > 0)
      rows.push({
        projectId: o.projectId,
        projectName: o.projectName,
        kind: 'awaitingApproval',
        count: o.awaitingClientApprovalCount,
      });
  }
  for (const o of overviews) {
    if (o.openIncidentsCount > 0)
      rows.push({
        projectId: o.projectId,
        projectName: o.projectName,
        kind: 'openIncidents',
        count: o.openIncidentsCount,
      });
  }
  return rows;
};

/**
 * Sprint 44, story-226. "Needs your attention": the client's read-only analogue of the
 * superintendent's "Needs your action". Counts per project only (no task titles, no write
 * control); not rendered at all when there is nothing, so no empty box and no reserved space.
 * Overdue is deliberately not here: it is shown on the card.
 */
export const ClientAttentionBlock: React.FC<ClientAttentionBlockProps> = ({
  overviews,
  onNavigate,
}) => {
  const t = useTranslation('clientProgress');
  const headingId = useId();
  const rows = buildAttentionRows(overviews);
  if (rows.length === 0) return null;

  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const shown = rows.slice(0, MAX_ROWS);
  const hidden = rows.length - shown.length;

  return (
    <section
      data-testid="client-attention-block"
      aria-labelledby={headingId}
      className="mb-3 ml-4 rounded-md border-l-[3px] border-indigo-600 bg-indigo-50 dark:bg-indigo-950/30 ring-1 ring-gray-900/5 dark:ring-white/10 p-3 flex-shrink-0">
      <div className="flex items-center justify-between gap-2">
        <h2
          id={headingId}
          className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
          <FlagOutlinedIcon sx={{ fontSize: 18 }} aria-hidden="true" />
          {t.attentionTitle}
        </h2>
        <span
          data-testid="client-attention-total"
          className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs font-medium text-white">
          {total}
        </span>
      </div>
      <ul className="mt-2 flex flex-col divide-y divide-indigo-100 dark:divide-indigo-900/50">
        {shown.map((row) => {
          const Icon =
            row.kind === 'awaitingApproval'
              ? HourglassEmptyOutlinedIcon
              : WarningAmberOutlinedIcon;
          return (
            <li key={`${row.projectId}:${row.kind}`}>
              <button
                type="button"
                data-testid={`client-attention-row-${row.kind}`}
                aria-label={fillTemplate(t.openProject, {
                  name: row.projectName,
                })}
                onClick={() => onNavigate(row.projectId, row.kind)}
                className="flex w-full items-center gap-2 py-2 text-left hover:bg-indigo-100/60 dark:hover:bg-indigo-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400">
                <Icon sx={{ fontSize: 18 }} aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-gray-900 dark:text-white">
                    {pluralize(
                      t,
                      row.kind === 'awaitingApproval'
                        ? 'attentionApproval'
                        : 'attentionIncident',
                      row.count,
                    )}
                  </span>
                  <span className="block truncate text-xs text-gray-600 dark:text-gray-300">
                    {row.projectName}
                  </span>
                </span>
                <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {hidden > 0 && (
        <p
          data-testid="client-attention-more"
          className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          {fillTemplate(t.attentionMore, { n: hidden })}
        </p>
      )}
    </section>
  );
};

export default ClientAttentionBlock;
